import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  MapPin, Plus, Camera, Search, Filter, Eye, Edit, Trash2, CheckCircle,
  Clock, Send, Calendar, Sparkles, X, Upload, Image as ImageIcon,
  Globe, Share2, AlertCircle, MoreVertical, ExternalLink
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  draft: { label: 'Draft', color: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300' },
  pending_approval: { label: 'Pending Approval', color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' },
  approved: { label: 'Approved', color: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' },
  scheduled: { label: 'Scheduled', color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400' },
  published: { label: 'Published', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' },
  failed: { label: 'Failed', color: 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400' }
};

const CheckInCard = ({ checkIn, onEdit, onDelete, onApprove, onView }) => {
  const status = statusConfig[checkIn.status] || statusConfig.draft;
  
  return (
    <Card className="dark:bg-slate-800/60 dark:border-slate-700 hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex gap-4">
          {/* Photo thumbnail */}
          <div className="w-24 h-24 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {checkIn.photo_urls && checkIn.photo_urls[0] ? (
              <img 
                src={checkIn.photo_urls[0].url} 
                alt={checkIn.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <Camera className="h-8 w-8 text-slate-400" />
            )}
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-medium dark:text-white truncate">{checkIn.title}</h3>
                <p className="text-sm text-muted-foreground dark:text-slate-400 flex items-center gap-1 mt-1">
                  <MapPin className="h-3 w-3" />
                  {checkIn.address}, {checkIn.city}, {checkIn.state}
                </p>
              </div>
              <Badge className={status.color}>{status.label}</Badge>
            </div>
            
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-2 line-clamp-2">
              {checkIn.description || 'No description'}
            </p>
            
            <div className="flex items-center gap-2 mt-3">
              {checkIn.keywords?.slice(0, 3).map((kw, idx) => (
                <Badge key={idx} variant="outline" className="text-xs">{kw}</Badge>
              ))}
              {checkIn.keywords?.length > 3 && (
                <Badge variant="outline" className="text-xs">+{checkIn.keywords.length - 3}</Badge>
              )}
            </div>
            
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground dark:text-slate-400">
                {checkIn.publish_targets?.website && (
                  <span className="flex items-center gap-1"><Globe className="h-3 w-3" /> Website</span>
                )}
                {checkIn.publish_targets?.social && (
                  <span className="flex items-center gap-1"><Share2 className="h-3 w-3" /> Social</span>
                )}
              </div>
              
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => onView(checkIn)}>
                  <Eye className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => onEdit(checkIn)}>
                  <Edit className="h-4 w-4" />
                </Button>
                {checkIn.status === 'draft' && (
                  <Button variant="ghost" size="sm" className="text-emerald-600" onClick={() => onApprove(checkIn)}>
                    <Send className="h-4 w-4" />
                  </Button>
                )}
                <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => onDelete(checkIn)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const CreateCheckInDialog = ({ open, onClose, onSave, editingCheckIn }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    service_type: '',
    keywords: [],
    publish_targets: { website: true, social: true },
    review_request_enabled: false
  });
  const [keywordInput, setKeywordInput] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (editingCheckIn) {
      setFormData({
        title: editingCheckIn.title || '',
        description: editingCheckIn.description || '',
        address: editingCheckIn.address || '',
        city: editingCheckIn.city || '',
        state: editingCheckIn.state || '',
        zip_code: editingCheckIn.zip_code || '',
        service_type: editingCheckIn.service_type || '',
        keywords: editingCheckIn.keywords || [],
        publish_targets: editingCheckIn.publish_targets || { website: true, social: true },
        review_request_enabled: editingCheckIn.review_request_enabled || false
      });
    } else {
      setFormData({
        title: '',
        description: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        service_type: '',
        keywords: [],
        publish_targets: { website: true, social: true },
        review_request_enabled: false
      });
    }
  }, [editingCheckIn, open]);

  const handleGenerateAI = async () => {
    if (!formData.title) {
      toast.error('Please enter a title first');
      return;
    }
    
    setGenerating(true);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const params = new URLSearchParams({
        title: formData.title,
        service_type: formData.service_type || 'RhinoShield',
        address: formData.address || 'Florida'
      });
      if (formData.keywords?.length) {
        formData.keywords.forEach(kw => params.append('keywords', kw));
      }
      
      const res = await axios.post(
        `${API}/marketing/ai/generate-description?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.description) {
        setFormData(prev => ({
          ...prev,
          description: res.data.description,
          keywords: res.data.keywords || prev.keywords
        }));
        toast.success('AI content generated!');
      }
    } catch (error) {
      toast.error('Failed to generate AI content');
    } finally {
      setGenerating(false);
    }
  };

  const addKeyword = () => {
    if (keywordInput.trim() && !formData.keywords.includes(keywordInput.trim())) {
      setFormData(prev => ({ ...prev, keywords: [...prev.keywords, keywordInput.trim()] }));
      setKeywordInput('');
    }
  };

  const removeKeyword = (kw) => {
    setFormData(prev => ({ ...prev, keywords: prev.keywords.filter(k => k !== kw) }));
  };

  const handleSave = () => {
    if (!formData.title || !formData.address) {
      toast.error('Title and address are required');
      return;
    }
    onSave(formData, editingCheckIn?.id);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto dark:bg-slate-800">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 dark:text-white">
            <Camera className="h-5 w-5 text-blue-500" />
            {editingCheckIn ? 'Edit Check-In' : 'Create New Check-In'}
          </DialogTitle>
          <DialogDescription className="dark:text-slate-400">
            Capture and share your completed work
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="dark:text-slate-200">Title *</Label>
              <Input
                placeholder="e.g., Beautiful RhinoShield transformation in Tampa"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="dark:bg-slate-700 dark:border-slate-600"
              />
            </div>
            
            <div className="col-span-2">
              <Label className="dark:text-slate-200">Address *</Label>
              <Input
                placeholder="123 Main Street"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="dark:bg-slate-700 dark:border-slate-600"
              />
            </div>
            
            <div>
              <Label className="dark:text-slate-200">City</Label>
              <Input
                placeholder="Tampa"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="dark:bg-slate-700 dark:border-slate-600"
              />
            </div>
            
            <div>
              <Label className="dark:text-slate-200">State</Label>
              <Input
                placeholder="FL"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className="dark:bg-slate-700 dark:border-slate-600"
              />
            </div>
            
            <div>
              <Label className="dark:text-slate-200">ZIP Code</Label>
              <Input
                placeholder="33601"
                value={formData.zip_code}
                onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                className="dark:bg-slate-700 dark:border-slate-600"
              />
            </div>
            
            <div>
              <Label className="dark:text-slate-200">Service Type</Label>
              <Select
                value={formData.service_type}
                onValueChange={(v) => setFormData({ ...formData, service_type: v })}
              >
                <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="RhinoShield">RhinoShield</SelectItem>
                  <SelectItem value="DeckSeal">Deck Seal</SelectItem>
                  <SelectItem value="PoolDeck">Pool Deck Coating</SelectItem>
                  <SelectItem value="Commercial">Commercial</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="col-span-2">
              <div className="flex items-center justify-between mb-2">
                <Label className="dark:text-slate-200">Description</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleGenerateAI}
                  disabled={generating}
                  className="gap-1"
                >
                  <Sparkles className="h-3 w-3" />
                  {generating ? 'Generating...' : 'AI Generate'}
                </Button>
              </div>
              <Textarea
                placeholder="Describe the project and transformation..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="dark:bg-slate-700 dark:border-slate-600"
              />
            </div>
            
            <div className="col-span-2">
              <Label className="dark:text-slate-200">Keywords</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  placeholder="Add keyword"
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                  className="dark:bg-slate-700 dark:border-slate-600"
                />
                <Button type="button" variant="outline" onClick={addKeyword}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.keywords.map((kw, idx) => (
                  <Badge key={idx} variant="secondary" className="gap-1">
                    {kw}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeKeyword(kw)} />
                  </Badge>
                ))}
              </div>
            </div>
            
            <div className="col-span-2">
              <Label className="dark:text-slate-200">Publish Targets</Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.publish_targets.website}
                    onChange={(e) => setFormData({
                      ...formData,
                      publish_targets: { ...formData.publish_targets, website: e.target.checked }
                    })}
                    className="rounded"
                  />
                  <Globe className="h-4 w-4" />
                  <span className="text-sm dark:text-slate-200">Website</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.publish_targets.social}
                    onChange={(e) => setFormData({
                      ...formData,
                      publish_targets: { ...formData.publish_targets, social: e.target.checked }
                    })}
                    className="rounded"
                  />
                  <Share2 className="h-4 w-4" />
                  <span className="text-sm dark:text-slate-200">Social Media</span>
                </label>
              </div>
            </div>
            
            <div className="col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.review_request_enabled}
                  onChange={(e) => setFormData({ ...formData, review_request_enabled: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm dark:text-slate-200">Enable review request for this check-in</span>
              </label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700">
            {editingCheckIn ? 'Save Changes' : 'Create Check-In'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const CheckIns = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [checkIns, setCheckIns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCheckIn, setEditingCheckIn] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchCheckIns = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const params = filterStatus !== 'all' ? `?status=${filterStatus}` : '';
      const res = await axios.get(`${API}/marketing/check-ins${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCheckIns(res.data || []);
    } catch (error) {
      toast.error('Failed to load check-ins');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchCheckIns();
  }, [fetchCheckIns]);

  useEffect(() => {
    if (searchParams.get('action') === 'create') {
      setDialogOpen(true);
    }
  }, [searchParams]);

  const handleSave = async (data, id) => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      if (id) {
        await axios.patch(`${API}/marketing/check-ins/${id}`, data, { headers });
        toast.success('Check-in updated!');
      } else {
        await axios.post(`${API}/marketing/check-ins`, data, { headers });
        toast.success('Check-in created!');
      }
      
      setDialogOpen(false);
      setEditingCheckIn(null);
      fetchCheckIns();
    } catch (error) {
      toast.error('Failed to save check-in');
    }
  };

  const handleDelete = async (checkIn) => {
    if (!window.confirm('Are you sure you want to delete this check-in?')) return;
    
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      await axios.delete(`${API}/marketing/check-ins/${checkIn.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Check-in deleted');
      fetchCheckIns();
    } catch (error) {
      toast.error('Failed to delete check-in');
    }
  };

  const handleSubmitForApproval = async (checkIn) => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      await axios.post(`${API}/marketing/check-ins/${checkIn.id}/submit-for-approval`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Submitted for approval!');
      fetchCheckIns();
    } catch (error) {
      toast.error('Failed to submit for approval');
    }
  };

  const filteredCheckIns = checkIns.filter(ci => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return ci.title?.toLowerCase().includes(q) ||
             ci.address?.toLowerCase().includes(q) ||
             ci.city?.toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold dark:text-white flex items-center gap-2">
              <MapPin className="h-8 w-8 text-blue-500" />
              Geo Check-Ins
            </h1>
            <p className="text-muted-foreground dark:text-slate-400 mt-1">
              Property check-ins for marketing content
            </p>
          </div>
          <Button onClick={() => { setEditingCheckIn(null); setDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" />
            New Check-In
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search check-ins..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 dark:bg-slate-800 dark:border-slate-700"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[180px] dark:bg-slate-800 dark:border-slate-700">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="pending_approval">Pending Approval</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="published">Published</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Check-Ins Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredCheckIns.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredCheckIns.map(checkIn => (
              <CheckInCard
                key={checkIn.id}
                checkIn={checkIn}
                onView={(ci) => toast.info('View details coming soon')}
                onEdit={(ci) => { setEditingCheckIn(ci); setDialogOpen(true); }}
                onDelete={handleDelete}
                onApprove={handleSubmitForApproval}
              />
            ))}
          </div>
        ) : (
          <Card className="dark:bg-slate-800/60">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Camera className="h-16 w-16 text-slate-300 dark:text-slate-600 mb-4" />
              <h3 className="text-lg font-medium dark:text-white">No check-ins yet</h3>
              <p className="text-muted-foreground dark:text-slate-400 mt-1">
                Create your first geo check-in to showcase completed work
              </p>
              <Button
                onClick={() => setDialogOpen(true)}
                className="mt-4 bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Check-In
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Create/Edit Dialog */}
        <CreateCheckInDialog
          open={dialogOpen}
          onClose={() => { setDialogOpen(false); setEditingCheckIn(null); }}
          onSave={handleSave}
          editingCheckIn={editingCheckIn}
        />
      </div>
    </Layout>
  );
};
